import {
    buttonVariants,
    Button as HeroButton,
    type ButtonProps as HeroButtonProps,
} from "@heroui/react";
import { Loader2 } from "lucide-react";
import { motion } from "motion/react";
import {
    type ComponentType,
    type ReactNode,
    useEffect,
    useEffectEvent,
    useRef,
    useState,
} from "react";
import { tv, type VariantProps } from "tailwind-variants";

import { designSystemTwMergeConfig } from "../../lib/twMergeConfig";
import { buttonTypographyClasses } from "./buttonStyleConfig";

const pendingLayoutTransition = {
    layout: { duration: 0.2, ease: "easeOut" as const },
    opacity: { duration: 0.2, ease: "easeOut" as const },
    width: { duration: 0.2, ease: "easeOut" as const },
};

function isFullWidthButton(variant: ButtonVariant, className?: string) {
    return variant === "onboarding" || className?.includes("w-full") === true;
}

const designSystemButtonVariants: ReturnType<typeof tv> = tv(
    {
        extend: buttonVariants,
        base: [
            "h-auto",
            "border",
            "transition-all",
            "disabled:cursor-not-allowed",
            "gap-12",
            "items-center",
            "align-center",
            "select-none",
        ],
        variants: {
            variant: {
                primary: [
                    ...buttonTypographyClasses,
                    "bg-components-button-primary-default-fill",
                    "hover:bg-components-button-primary-hover-fill",
                    "active:bg-components-button-primary-pressed-fill",
                    "disabled:bg-components-button-primary-disabled-fill",
                    "text-components-button-primary-default-text",
                    "hover:text-components-button-primary-hover-text",
                    "active:text-components-button-primary-pressed-text",
                    "disabled:text-components-button-primary-disabled-text",
                    "border-components-button-primary-default-border-color",
                    "hover:border-components-button-primary-hover-border-color",
                    "active:border-components-button-primary-pressed-border-color",
                    "disabled:border-components-button-primary-disabled-border-color",
                    "rounded-button",
                    "px-32",
                    "py-16",
                ],
                secondary: [
                    ...buttonTypographyClasses,
                    "bg-components-button-secondary-default-fill",
                    "hover:bg-components-button-secondary-hover-fill",
                    "active:bg-components-button-secondary-pressed-fill",
                    "disabled:bg-components-button-secondary-disabled-fill",
                    "text-components-button-secondary-default-text",
                    "hover:text-components-button-secondary-hover-text",
                    "active:text-components-button-secondary-pressed-text",
                    "disabled:text-components-button-secondary-disabled-text",
                    "border-components-button-secondary-default-border-color",
                    "hover:border-components-button-secondary-hover-border-color",
                    "active:border-components-button-secondary-pressed-border-color",
                    "disabled:border-components-button-secondary-disabled-border-color",
                    "rounded-button",
                    "px-24",
                    "py-12",
                ],
                onboarding: [
                    ...buttonTypographyClasses,
                    "bg-components-button-onboarding-default-fill",
                    "hover:bg-components-button-onboarding-hover-fill",
                    "active:bg-components-button-onboarding-pressed-fill",
                    "disabled:bg-components-button-onboarding-disabled-fill",
                    "text-components-button-onboarding-default-text",
                    "hover:text-components-button-onboarding-hover-text",
                    "active:text-components-button-onboarding-pressed-text",
                    "disabled:text-components-button-onboarding-disabled-text",
                    "border-components-button-onboarding-default-border-color",
                    "hover:border-components-button-onboarding-hover-border-color",
                    "active:border-components-button-onboarding-pressed-border-color",
                    "disabled:border-components-button-onboarding-disabled-border-color",
                    "rounded-rounded",
                    "rounded-br-sharp",
                    "p-[20px]",
                    "!h-[56px]",
                    "w-full",
                    "font-label",
                    "font-weight-label-medium",
                    "text-mobile-button-text",
                    "sm-md:text-desktop-button-text",
                    "normal-case!",
                    "shadow-sm",
                    "active:shadow-none",
                ],
                inverse: [
                    ...buttonTypographyClasses,
                    "bg-components-button-inverse-default-fill",
                    "hover:bg-components-button-inverse-hover-fill",
                    "active:bg-components-button-inverse-pressed-fill",
                    "disabled:bg-components-button-inverse-disabled-fill",
                    "text-components-button-inverse-default-text",
                    "hover:text-components-button-inverse-hover-text",
                    "active:text-components-button-inverse-pressed-text",
                    "disabled:text-components-button-inverse-disabled-text",
                    "border-components-button-inverse-default-border-color",
                    "hover:border-components-button-inverse-hover-border-color",
                    "active:border-components-button-inverse-pressed-border-color",
                    "disabled:border-components-button-inverse-disabled-border-color",
                    "rounded-button",
                    "px-32",
                    "py-16",
                    "text-mobile-button-small",
                    "sm-md:text-desktop-button-small",
                ],
                "icon.default": [
                    ...buttonTypographyClasses,
                    "bg-components-button-icon-default-default-fill",
                    "hover:bg-components-button-icon-default-hover-fill",
                    "active:bg-components-button-icon-default-pressed-fill",
                    "text-components-button-icon-default-default-icon",
                    "hover:text-components-button-icon-default-hover-icon",
                    "active:text-components-button-icon-default-pressed-icon",
                    "border-transparent",
                    "rounded-button",
                    "px-16",
                    "py-12",
                ],
                "icon.filled": [
                    ...buttonTypographyClasses,
                    "bg-components-button-icon-filled-default-fill",
                    "hover:bg-components-button-icon-filled-hover-fill",
                    "active:bg-components-button-icon-filled-pressed-fill",
                    "text-components-button-icon-filled-default-icon",
                    "hover:text-components-button-icon-filled-hover-icon",
                    "active:text-components-button-icon-filled-pressed-icon",
                    "border-transparent",
                    "rounded-button",
                    "px-16",
                    "py-12",
                ],
                "icon.inverse": [
                    ...buttonTypographyClasses,
                    "bg-components-button-icon-inverse-default-fill",
                    "hover:bg-components-button-icon-inverse-hover-fill",
                    "active:bg-components-button-icon-inverse-pressed-fill",
                    "text-components-button-icon-inverse-default-icon",
                    "hover:text-components-button-icon-inverse-hover-icon",
                    "active:text-components-button-icon-inverse-pressed-icon",
                    "border-transparent",
                    "rounded-button",
                    "px-16",
                    "py-12",
                ],
                tertiary: [
                    ...buttonTypographyClasses,
                    "bg-transparent",
                    "hover:bg-transparent",
                    "active:bg-transparent",
                    "disabled:bg-transparent",
                    "text-text-primary",
                    "hover:text-text-tertiary",
                    "active:text-text-secondary",
                    "disabled:text-text-tertiary",
                    "border-transparent",
                    "hover:border-transparent",
                    "active:border-transparent",
                    "disabled:border-transparent",
                    "rounded-button",
                    "px-16",
                    "py-12",
                ],
                text: [
                    ...buttonTypographyClasses,
                    "border-0",
                    "h-auto",
                    "min-h-0",
                    "gap-8",
                    "p-0",
                    "transition-all duration-150 ease-in-out",
                    "bg-components-button-text-default-fill",
                    "hover:bg-components-button-text-hover-fill",
                    "active:bg-components-button-text-pressed-fill",
                    "disabled:bg-components-button-text-disabled-fill",
                    "text-components-button-text-default-text",
                    "hover:text-components-button-text-hover-text",
                    "active:text-components-button-text-pressed-text",
                    "disabled:text-components-button-text-disabled-text",
                    "border-components-button-text-default-border-color",
                    "hover:border-components-button-text-hover-border-color",
                    "active:border-components-button-text-pressed-border-color",
                    "disabled:border-components-button-text-disabled-border-color",
                    "rounded-button",
                    "normal-case!",
                    "font-label!",
                ],
            },
        },
        defaultVariants: {
            variant: "primary",
        },
    },
    designSystemTwMergeConfig,
);

export type ButtonVariants = VariantProps<typeof designSystemButtonVariants>;
/** Includes deprecated `tertiary` — prefer `text` for ghost/text-only actions. */
export type ButtonVariant = ButtonVariants["variant"];

export interface ButtonProps
    extends Omit<
            HeroButtonProps,
            | "className"
            | "variant"
            | "children"
            | "onClick"
            | "onAnimationStart"
            | "onAnimationEnd"
            | "onAnimationIteration"
            | "onAnimationStartCapture"
            | "onAnimationEndCapture"
            | "onAnimationIterationCapture"
            | "onHoverStart"
            | "onHoverEnd"
        >,
        ButtonVariants {
    className?: string;
    children?: ReactNode;
    isPending?: boolean;
    /** When false, label overflow is not clipped with CSS ellipsis (default true). */
    truncate?: boolean;
}

/**
 * Token-backed Button component using HeroUI as the base primitive.
 * Styling is composed with Tailwind Variants over design-system token CSS variables.
 */
export function Button({
    variant = "primary",
    className,
    children,
    isDisabled,
    isPending,
    truncate = true,
    ...props
}: ButtonProps) {
    const buttonClassName = designSystemButtonVariants({
        variant,
        className: [
            isFullWidthButton(variant, className)
                ? "inline-flex w-full min-w-0 max-w-full overflow-hidden"
                : "inline-flex min-w-0 max-w-full overflow-hidden",
            className,
        ]
            .filter(Boolean)
            .join(" "),
    });

    return (
        <MotionHeroButton
            layout="size"
            transition={pendingLayoutTransition}
            className={buttonClassName}
            isDisabled={isDisabled}
            isPending={isPending}
            {...props}
        >
            <ButtonContent isPending={isPending} truncate={truncate}>
                {children}
            </ButtonContent>
        </MotionHeroButton>
    );
}

type MotionConflictingHeroButtonProps =
    | "onAnimationStart"
    | "onAnimationEnd"
    | "onAnimationIteration"
    | "onAnimationStartCapture"
    | "onAnimationEndCapture"
    | "onAnimationIterationCapture"
    | "onHoverStart"
    | "onHoverEnd";

type MotionLayoutProps = {
    layout?: "size";
    transition?: typeof pendingLayoutTransition;
};

type MotionHeroButtonProps = Omit<
    HeroButtonProps,
    "className" | "variant" | "onClick" | MotionConflictingHeroButtonProps
> &
    MotionLayoutProps & {
        className?: string;
        variant?: ButtonVariant;
    };

const MotionHeroButton = motion.create(HeroButton) as ComponentType<
    MotionHeroButtonProps & {
        children?: HeroButtonProps["children"];
    }
>;

const PENDING_SPINNER_DELAY_MS = 1000;

function ButtonContent({
    children,
    isPending,
    truncate = true,
}: {
    children: ReactNode;
    isPending?: boolean;
    truncate?: boolean;
}) {
    const pendingSessionRef = useRef(0);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const showSpinnerRef = useRef(false);
    const [showSpinner, setShowSpinner] = useState(false);

    const setSpinnerVisible = useEffectEvent((next: boolean) => {
        if (showSpinnerRef.current === next) {
            return;
        }

        showSpinnerRef.current = next;
        setShowSpinner(next);
    });

    useEffect(
        function pendingSpinnerEffect() {
            if (timerRef.current !== null) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }

            if (!isPending) {
                setSpinnerVisible(false);
                return;
            }

            const session = pendingSessionRef.current + 1;
            pendingSessionRef.current = session;

            timerRef.current = setTimeout(() => {
                if (pendingSessionRef.current === session) {
                    setSpinnerVisible(true);
                }
            }, PENDING_SPINNER_DELAY_MS);

            return function clearPendingSpinnerTimer() {
                if (timerRef.current !== null) {
                    clearTimeout(timerRef.current);
                    timerRef.current = null;
                }

                pendingSessionRef.current += 1;
                setSpinnerVisible(false);
            };
        },
        [isPending],
    );

    const isSpinnerVisible = isPending === true && showSpinner;

    return (
        <motion.span
            layout
            transition={pendingLayoutTransition}
            className="inline-flex w-full min-w-0 max-w-full items-center justify-center"
        >
            <motion.span
                transition={pendingLayoutTransition}
                className={
                    truncate
                        ? // `text-overflow` only applies to block containers, so a
                          // label without icons drops out of flex to get the ellipsis.
                          "not-has-[svg]:block inline-flex min-w-0 max-w-full items-center justify-center gap-4 truncate"
                        : "inline-flex min-w-0 max-w-full items-center justify-center gap-4 whitespace-nowrap"
                }
            >
                {children}
            </motion.span>
            <motion.span
                layout
                transition={pendingLayoutTransition}
                initial={{ width: 0, opacity: 0, marginLeft: 0 }}
                animate={{
                    width: isSpinnerVisible ? 16 : 0,
                    opacity: isSpinnerVisible ? 1 : 0,
                    marginLeft: isSpinnerVisible ? 8 : 0,
                }}
                className="inline-flex w-fit shrink-0 items-center justify-center overflow-hidden"
                data-slot="spinner"
                aria-hidden={!isSpinnerVisible}
            >
                <Loader2
                    className="size-16 shrink-0 animate-spin text-current motion-reduce:animate-none"
                    aria-hidden="true"
                />
            </motion.span>
        </motion.span>
    );
}
